import React, { FunctionComponent, Suspense, useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { APP_URLS, currencyCode } from '../../../consts';
import { useSnackbar } from '../../../hooks/snackbar';
import { useDeviceInfo } from '../../../hooks/useDeviceInfo';
import { useUserContext } from '../../../providers/UserProvider';
import { selectGameLaunchUrl } from '../../../state/game';
import { setGameData, setGameLaunchTime, setGameLaunchUrl } from '../../../state/game/reducers';
import { getGameAspectRatio } from '../../../utils/games/games.utils';
import { Spinner } from '../../shared/Spinner';
import { GameSearchSpineZone } from '../GameSearch';
import { GameSessionInGame } from '../GameSession';
import { GameSessionParallel } from '../GameSession';
import { Game, GameChannel } from '../games.model';
import { retrieveGameLaunchUrl } from '../service';
import { GameLauncherMenu } from './GameLauncherMenu';
import GamingPerformancePrompt from './GamingPerformancePrompt';
import { useGameSession, useGameDetailsBasePath } from '../../../hooks/games';
import { useTranslations } from '../../../hooks/useTranslationsHelper';
import './GameLauncher.scss';
import { useSessionContextUpdatedSubscription } from '../../../libs/graphql/baseAppAPI/subscriptions/__generated__/session-context-updated.generated';
import useDestroyUserSession from '../../../hooks/auth/useDestroyUserSession';
import { getGameUrl } from '../helper';

export type GameContainerProps = {
  game: Game;
  mode: 'demo' | 'real';
};
/**
 * Component will attempt to launch provided game and display overlay with actions/session info.
 *
 * TODO: What is the unhappy path?  useGameLauncher returns request failure data - use this when unhappy path is defined.
 */
export const GameLauncher: FunctionComponent<GameContainerProps> = ({ game }) => {
  const { t } = useTranslations();
  const auth = useAuth();
  const { addSnack } = useSnackbar();
  const { isMobileDevice } = useDeviceInfo();
  const { handleGameStartDuringBreak } = useGameSession();
  const gameChannel: GameChannel = isMobileDevice ? GameChannel.Mobile : GameChannel.Desktop;
  const dispatch = useDispatch();
  const gameContainer = useRef<HTMLDivElement>(null);
  const iframeId = 'game-iframe';
  const navigate = useNavigate();
  const { userProfileId } = useUserContext();
  const playerId = userProfileId || (auth.user?.profile?.user_profile_id as string);
  const launchUrl = useSelector(selectGameLaunchUrl);
  const { friendlyUrlName, displayName } = game;
  const { logoutUser } = useDestroyUserSession();
  const { basePath: gameDetailsBasePath } = useGameDetailsBasePath();

  // TODO: add missing logic
  const mocks = {
    gameAspectRatio: getGameAspectRatio(game, gameChannel),
    isLaunchDataInitialised: true,
    isFetching: false,
    gameChannel: 'Desktop',
    isMobileDevice: false,
    gameBackgroundImage: game.gameplayBackground.imgUrl || '',
  };

  const [showPerformancePrompt, setShowPerformancePrompt] = useState(false);
  const [showParallelPrompt, setShowParallelPrompt] = useState(false);
  const [showGameSearch, setShowGameSearch] = useState<boolean>(false);
  const [showMenu, setShowMenu] = useState<boolean>(false);

  const { data } = useSessionContextUpdatedSubscription({
    variables: {
      userProfileId,
    },
  });

  useEffect(() => {
    const additionalProperties = JSON.parse(data?.sessionContextUpdated?.current?.additionalProperties || '{}');
    const isActiveCasinoMessage =
      data?.sessionContextUpdated?.mediatorType === 'casino' &&
      data?.sessionContextUpdated?.current?.status === 'Active';

    console.log(JSON.stringify(data, null, 2));

    if (additionalProperties && isActiveCasinoMessage) {
      // Keep session active when receiving game activity as we can't detect user interactions
      if (auth.user?.expired) {
        const refreshToken = async (): Promise<void> => {
          try {
            await auth.signinSilent();
          } catch (error) {
            console.error('Failed to refresh auth token:', error);
            logoutUser();
          }
        };

        refreshToken();
      }

      if (String(game.gameId) !== String(additionalProperties.Casino.GameExternalId)) {
        console.log('new alternate casino session detected, going to home');
        addSnack({ type: 'warning', message: 'Multiple game sessions detected' });
        navigate(APP_URLS.home);
      }
    }
  }, [data]);

  useEffect(() => {
    // Set current game data (used to match against session data)
    dispatch(setGameData(game));
    dispatch(setGameLaunchTime());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [friendlyUrlName]);

  const getGameLaunchUrl = useCallback(async (): Promise<void> => {
    try {
      if (auth.user?.expired) {
        await auth.signinSilent();
      }

      const launchUrl = await retrieveGameLaunchUrl({
        userProfileId: playerId,
        gameExternalId: game.gameId,
        currencyCode,
        gameProvider: String(game.provider?.mediatorId),
      });

      dispatch(setGameLaunchUrl(launchUrl.url));

      return;
    } catch (error) {
      console.error('retrieveGameLaunchUrl error: ', error);
      addSnack({
        type: 'error',
        message: t('errors.general'),
      });
      navigate(getGameUrl(friendlyUrlName, undefined, { basePath: gameDetailsBasePath }));
    }
  }, [friendlyUrlName, gameDetailsBasePath]);

  useEffect(() => {
    handleGameStartDuringBreak();

    document.body.style.setProperty('overflow', 'hidden');

    return () => {
      // setTimeout is used to avoid redefining overflow property in TouchDrawer
      setTimeout(() => {
        document.body.style.setProperty('overflow', null);
      }, 0);
    };
  }, []);

  useEffect(() => {
    getGameLaunchUrl();
  }, [friendlyUrlName, getGameLaunchUrl]);

  const pragGamesWithScroll = ['vpdt11', 'vpsc10', 'vpmr9', 'vpdr7', 'vplfl6', 'vppso4', 'vpfh3'];

  const scrollAttribute = useCallback((): 'yes' | 'no' => {
    return pragGamesWithScroll.includes(game.gameId) ? 'yes' : 'no';
  }, [game.gameId]);

  return (
    <>
      <Suspense fallback={<Spinner />}>
        <GamingPerformancePrompt
          show={showPerformancePrompt}
          onClose={(accepted): void => {
            if (accepted) {
              console.log('createGameLaunch');
            } else {
              // Byee - casino home
              navigate(APP_URLS.home);
            }

            setShowPerformancePrompt(false);
          }}
        />
      </Suspense>

      <GameSessionParallel
        show={showParallelPrompt}
        onClose={(): void => navigate(-1)}
        onPrimary={(): void => {
          setShowParallelPrompt(false);
          console.log('createGameLaunch');
        }}
      />

      <GameSessionInGame />

      <div className={`game-launcher game-launcher--${mocks.gameChannel.toLowerCase()}-channel`}>
        <GameLauncherMenu
          haveLaunched={!!launchUrl}
          iframeId={iframeId}
          onOpenGameSearch={(): void => setShowGameSearch(true)}
          setShowMenu={setShowMenu}
          showMenu={showMenu}
        >
          <div ref={gameContainer} className="game-launcher__game-container" style={{ overflow: 'hidden' }}>
            <div
              data-testid="game-container-inner"
              style={mocks?.gameBackgroundImage ? { backgroundImage: `url(${mocks?.gameBackgroundImage})` } : {}}
              className="game-launcher__game-container-inner"
            >
              <div className="game-launcher__game-container-content">
                {Boolean(launchUrl) && (
                  <iframe
                    className="game-launcher__iframe"
                    data-testid={iframeId}
                    id={iframeId}
                    src={launchUrl}
                    title={displayName}
                    allow="xr-spatial-tracking"
                    scrolling={scrollAttribute()}
                  />
                )}
              </div>
            </div>
          </div>
        </GameLauncherMenu>
      </div>
      <GameSearchSpineZone
        isOpen={showGameSearch}
        setShowMenu={setShowMenu}
        onCloseGameSearch={(): void => setShowGameSearch(false)}
      />
    </>
  );
};
